import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../api/axiosConfig';
import './GpsCapturePage.css';

function GpsCapturePage({ onLogout }) {
  const [zones, setZones] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedZone, setSelectedZone] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [isLoadingZones, setIsLoadingZones] = useState(false);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [captureStatus, setCaptureStatus] = useState('');
  const [currentPosition, setCurrentPosition] = useState(null);
  const [userName, setUserName] = useState('');
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [videoStream, setVideoStream] = useState(null);
  const [gpsFilter, setGpsFilter] = useState('all'); // 'all', 'with', 'without'
  const [clientSearch, setClientSearch] = useState('');
  const videoRef = React.useRef(null);
  const canvasRef = React.useRef(null);

  // Peut créer un nouveau client à prospecter (réservé aux marchandiseurs de Douala)
  const [canCreerProspect, setCanCreerProspect] = useState(false);
  const [showNouveauClientForm, setShowNouveauClientForm] = useState(false);
  const [isCreatingProspect, setIsCreatingProspect] = useState(false);
  const [commerciaux, setCommerciaux] = useState([]);
  const [nouveauClient, setNouveauClient] = useState({
    nom_client: '',
    contact: '',
    typologie: '',
    localisation: '',
    lieu_dit: '',
    est_gms: false,
    type_client: 'A_PROSPECTER',
    client_direct_nom: '',
    commercial_id: ''
  });

  // Récupérer le nom de l'utilisateur connecté
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await axiosInstance.get('/users/me/');
        setUserName(response.data.nom);
        const zoneChef = response.data?.merchandiser_profile?.chef_zone?.zone || '';
        const isDouala = zoneChef.toUpperCase().trim().startsWith('DOUALA');
        setCanCreerProspect(isDouala);
        if (isDouala) {
          try {
            const commerciauxRes = await axiosInstance.get('/merchandiser/commerciaux-douala');
            setCommerciaux(commerciauxRes.data || []);
          } catch (err) {
            console.error('Erreur lors du chargement des commerciaux:', err);
          }
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des infos utilisateur:', error);
      }
    };
    fetchUserInfo();
  }, []);

  const resetNouveauClientForm = () => {
    setNouveauClient({
      nom_client: '',
      contact: '',
      typologie: '',
      localisation: '',
      lieu_dit: '',
      est_gms: false,
      type_client: 'A_PROSPECTER',
      client_direct_nom: '',
      commercial_id: ''
    });
  };

  // Créer le nouveau client (en attente de validation), puis passer à l'étape géolocalisation/photo
  const handleCreerNouveauClient = async (e) => {
    e.preventDefault();
    if (!nouveauClient.nom_client.trim()) {
      alert('Le nom du client est obligatoire');
      return;
    }
    setIsCreatingProspect(true);
    try {
      const response = await axiosInstance.post('/merchandiser/clients/prospect', {
        ...nouveauClient,
        commercial_id: nouveauClient.commercial_id ? parseInt(nouveauClient.commercial_id, 10) : null,
        zone: selectedZone
      });
      const client = response.data;
      setShowNouveauClientForm(false);
      resetNouveauClientForm();
      setSelectedClient(client);
      setCaptureStatus('');
      setCurrentPosition(null);
    } catch (error) {
      console.error('Erreur création client:', error);
      alert(error.response?.data?.detail || "Erreur lors de la création du client");
    } finally {
      setIsCreatingProspect(false);
    }
  };

  // Charger les zones au démarrage
  const fetchZones = useCallback(async () => {
    try {
      setIsLoadingZones(true);
      const response = await axiosInstance.get('/zones/');
      setZones(response.data);
    } catch (error) {
      console.error('Erreur de chargement des zones:', error);
      setCaptureStatus('Erreur lors du chargement des zones');
    } finally {
      setIsLoadingZones(false);
    }
  }, []);

  // Charger les clients d'une zone
  const fetchClientsByZone = useCallback(async (zone) => {
    if (!zone) {
      setClients([]);
      return;
    }
    try {
      setIsLoadingClients(true);
      const response = await axiosInstance.get(`/clients/?zone=${zone}`);
      setClients(response.data);
    } catch (error) {
      console.error('Erreur de chargement des clients:', error);
      setCaptureStatus('Erreur lors du chargement des clients');
    } finally {
      setIsLoadingClients(false);
    }
  }, []);

  useEffect(() => {
    fetchZones();
  }, [fetchZones]);

  // Quand une zone est sélectionnée, charger ses clients
  const handleZoneChange = (e) => {
    const zone = e.target.value;
    setSelectedZone(zone);
    setSelectedClient(null);
    setClientSearch('');
    setCaptureStatus('');
    setCurrentPosition(null);
    fetchClientsByZone(zone);
  };

  // Sélectionner un client
  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setCaptureStatus('');
    setCurrentPosition(null);
  };

  // Capturer les coordonnées GPS actuelles
  const captureCoordinates = () => {
    if (!selectedClient) {
      alert('Veuillez d\'abord sélectionner un client');
      return;
    }

    if (!navigator.geolocation) {
      setCaptureStatus('❌ La géolocalisation n\'est pas supportée par votre navigateur');
      return;
    }

    setIsCapturing(true);
    setCaptureStatus('🔄 Recherche de votre position...');

    navigator.geolocation.getCurrentPosition(
      // Succès
      (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        setCurrentPosition({ latitude, longitude, accuracy });

        setCaptureStatus(`✅ Position trouvée ! Précision: ±${Math.round(accuracy)}m`);

        // Demander confirmation avant d'enregistrer
        confirmAndSaveCoordinates(latitude, longitude);
      },
      // Erreur
      (error) => {
        setIsCapturing(false);
        let errorMsg = '❌ Impossible de récupérer votre position';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMsg = '❌ Accès à la géolocalisation refusé. Veuillez autoriser l\'accès à votre position dans les paramètres du navigateur.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMsg = '❌ Information de position non disponible';
            break;
          case error.TIMEOUT:
            errorMsg = '❌ Délai expiré. Réessayez.';
            break;
        }
        setCaptureStatus(errorMsg);
      },
      // Options
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0
      }
    );
  };

  // Confirmer et enregistrer les coordonnées
  const confirmAndSaveCoordinates = async (lat, lng) => {
    // Vérifier qu'une photo a été prise
    if (!photo) {
      alert('❌ Veuillez prendre une photo du point de vente avant d\'enregistrer les coordonnées.');
      setCaptureStatus('❌ Photo obligatoire pour enregistrer les coordonnées');
      return;
    }

    const confirmed = window.confirm(
      `📍 Enregistrer ces coordonnées pour "${selectedClient.nom_client}" ?\n\n` +
      `Latitude: ${lat.toFixed(6)}\n` +
      `Longitude: ${lng.toFixed(6)}\n` +
      `📷 Photo: Oui\n\n` +
      `✅ Assurez-vous d'être bien devant le point de vente !`
    );

    if (confirmed) {
      try {
        setIsCapturing(true);
        setCaptureStatus('🔄 Enregistrement en cours...');

        // 1. Enregistrer les coordonnées avec historique
        await axiosInstance.post(`/clients/${selectedClient.id}/geolocaliser`, {
          latitude: lat,
          longitude: lng,
          precision: currentPosition?.accuracy || 0
        });

        // 2. Uploader la photo
        await uploadPhoto(lat, lng, currentPosition?.accuracy);

        setCaptureStatus('✅ Coordonnées et photo enregistrées avec succès !');

        // Mettre à jour la liste des clients
        await fetchClientsByZone(selectedZone);

        // Réinitialiser après 2 secondes
        setTimeout(() => {
          setCaptureStatus('');
          setCurrentPosition(null);
          setSelectedClient(null);
          setPhoto(null);
          setPhotoPreview(null);
        }, 2000);

      } catch (error) {
        console.error('Erreur lors de l\'enregistrement:', error);
        setCaptureStatus('❌ Erreur lors de l\'enregistrement');
      } finally {
        setIsCapturing(false);
      }
    } else {
      setCaptureStatus('ℹ️ Enregistrement annulé');
      setIsCapturing(false);
    }
  };

  // Ouvrir la caméra pour prendre une photo
  const openCamera = async () => {
    try {
      if (!selectedClient) {
        alert('Veuillez d\'abord sélectionner un client');
        return;
      }

      // Accéder à la caméra
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment', // Caméra arrière sur mobile
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });

      setVideoStream(stream);
      setShowCameraModal(true);

      // Attendre que le modal soit monté pour attacher le flux vidéo
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
        }
      }, 100);

    } catch (error) {
      console.error('Erreur d\'accès à la caméra:', error);
      alert('Impossible d\'accéder à la caméra. Vérifiez les permissions ou utilisez un appareil compatible.');
    }
  };

  // Capturer une photo depuis le flux vidéo
  const capturePhotoFromCamera = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    // Définir la taille du canvas à la taille vidéo
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Dessiner l'image actuelle de la vidéo sur le canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convertir le canvas en Blob (fichier image)
    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `photo_${selectedClient.id}_${Date.now()}.jpg`, {
          type: 'image/jpeg'
        });
        setPhoto(file);
        setPhotoPreview(URL.createObjectURL(file));
        closeCamera();
      }
    }, 'image/jpeg', 0.95);
  };

  // Fermer la caméra et le modal
  const closeCamera = () => {
    // Arrêter le flux vidéo
    if (videoStream) {
      videoStream.getTracks().forEach(track => track.stop());
      setVideoStream(null);
    }
    setShowCameraModal(false);
  };

  // Gérer la sélection de photo (fallback si caméra pas disponible)
  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhoto(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  // Upload la photo vers le serveur
  const uploadPhoto = async (lat, lng, accuracy) => {
    if (!photo || !selectedClient) return;

    setIsUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', photo);
      if (lat) formData.append('latitude', lat);
      if (lng) formData.append('longitude', lng);
      if (accuracy) formData.append('precision', accuracy);

      await axiosInstance.post(`/clients/${selectedClient.id}/upload-photo-pdv`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log('Photo uploadée avec succès');
    } catch (error) {
      console.error('Erreur lors de l\'upload de la photo:', error);
      throw error;
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  // Supprimer la photo sélectionnée
  const clearPhoto = () => {
    setPhoto(null);
    setPhotoPreview(null);
    closeCamera();
  };

  // Nettoyer les ressources quand le composant est démonté
  useEffect(() => {
    return () => {
      if (videoStream) {
        videoStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [videoStream]);

  // Changer de client (avec ou sans GPS)
  const filterClients = (showOnlyWithoutGps) => {
    // Cette fonction peut être étendue pour filtrer les clients
    // Pour l'instant, tous les clients sont affichés
  };

  // Calculer les statistiques
  const clientsWithGps = clients.filter(c => c.latitude && c.longitude).length;
  const clientsWithoutGps = clients.length - clientsWithGps;

  const filteredClients = clients
    .filter(c => !c.latitude || !c.longitude)
    .filter(c => c.nom_client.toLowerCase().includes(clientSearch.toLowerCase()));

  return (
    <div className="gps-capture-page">
      <header className="app-header">
        <div className="header-content">
          <h1>
            <img src="/goutte-sp.png" className="logo-icon" />
            Géolocalisation
          </h1>
          <div className="user-info">
            <span>{userName}</span>
            <button onClick={onLogout} className="logout-btn">Déconnexion</button>
          </div>
        </div>
      </header>

      <main className="main-content">
        <div className="page-description">
          <h2>Géolocalisation des Points de Vente</h2>
          <p>Sélectionnez une zone, choisissez le client où vous êtes, puis capturez les coordonnées GPS</p>
        </div>

        {/* Étape 1: Sélection de la zone */}
        <div className="step-container">
          <div className="step-header">
            <span className="step-number">1</span>
            <h3>Choisissez une zone</h3>
          </div>
          <div className="step-content">
            {isLoadingZones ? (
              <div className="loading">Chargement des zones...</div>
            ) : (
              <select
                value={selectedZone}
                onChange={handleZoneChange}
                className="zone-select"
              >
                <option value="">-- Sélectionner une zone --</option>
                {zones.map((z, idx) => (
                  <option key={idx} value={z.zone}>{z.zone}</option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* Étape 2: Liste des clients de la zone */}
        {selectedZone && (
          <div className="step-container">
            <div className="step-header">
              <span className="step-number">2</span>
              <h3>Choisissez le client où vous êtes</h3>
            </div>
            <div className="step-content">
              {canCreerProspect && !showNouveauClientForm && (
                <button
                  type="button"
                  className="photo-button secondary"
                  style={{ marginBottom: '15px' }}
                  onClick={() => {
                    setShowNouveauClientForm(true);
                    setSelectedClient(null);
                  }}
                >
                  ➕ Nouveau client (pas encore dans la liste)
                </button>
              )}

              {showNouveauClientForm && (
                <form onSubmit={handleCreerNouveauClient} className="nouveau-client-form">
                  <h4>Nouveau client à prospecter</h4>

                  <div className="form-field">
                    <label>Nom du client *</label>
                    <input
                      type="text"
                      value={nouveauClient.nom_client}
                      onChange={e => setNouveauClient({ ...nouveauClient, nom_client: e.target.value })}
                      placeholder="Ex: Boutique Chez Marie"
                      required
                    />
                  </div>

                  <div className="form-field">
                    <label>Contact (téléphone)</label>
                    <input
                      type="text"
                      value={nouveauClient.contact}
                      onChange={e => setNouveauClient({ ...nouveauClient, contact: e.target.value })}
                      placeholder="Ex: 6XX XX XX XX"
                    />
                  </div>

                  <div className="form-field">
                    <label>Typologie</label>
                    <input
                      type="text"
                      value={nouveauClient.typologie}
                      onChange={e => setNouveauClient({ ...nouveauClient, typologie: e.target.value })}
                      placeholder="Ex: Boutique, Bar, Restaurant..."
                    />
                  </div>

                  <div className="form-field">
                    <label>Localisation</label>
                    <input
                      type="text"
                      value={nouveauClient.localisation}
                      onChange={e => setNouveauClient({ ...nouveauClient, localisation: e.target.value })}
                      placeholder="Ex: Akwa, Bonabéri..."
                    />
                  </div>

                  <div className="form-field">
                    <label>Lieu-dit / Secteur</label>
                    <input
                      type="text"
                      value={nouveauClient.lieu_dit}
                      onChange={e => setNouveauClient({ ...nouveauClient, lieu_dit: e.target.value })}
                      placeholder="Ex: Carrefour Ndokoti"
                    />
                  </div>

                  <div className="form-field">
                    <label>Type de client</label>
                    <select
                      value={nouveauClient.type_client}
                      onChange={e => setNouveauClient({
                        ...nouveauClient,
                        type_client: e.target.value,
                        client_direct_nom: e.target.value === 'INDIRECT' ? nouveauClient.client_direct_nom : ''
                      })}
                    >
                      <option value="A_PROSPECTER">🔍 À PROSPECTER</option>
                      <option value="DIRECT">✅ DIRECT</option>
                      <option value="INDIRECT">🔄 INDIRECT</option>
                    </select>
                  </div>

                  <div className="form-field">
                    <label>Commercial</label>
                    <select
                      value={nouveauClient.commercial_id}
                      onChange={e => setNouveauClient({ ...nouveauClient, commercial_id: e.target.value })}
                    >
                      <option value="">-- Sélectionner un commercial --</option>
                      {commerciaux.map((com) => (
                        <option key={com.id} value={com.id}>{com.nom}</option>
                      ))}
                    </select>
                  </div>

                  {nouveauClient.type_client === 'INDIRECT' && (
                    <div className="form-field">
                      <label>Nom du client direct</label>
                      <input
                        type="text"
                        value={nouveauClient.client_direct_nom}
                        onChange={e => setNouveauClient({ ...nouveauClient, client_direct_nom: e.target.value })}
                        placeholder="Nom du client direct associé"
                      />
                    </div>
                  )}

                  <div className="form-hint">
                    ℹ️ Ce client sera enregistré "en attente" et devra être validé par l'admin avant d'être utilisable.
                  </div>

                  <div className="form-actions">
                    <button type="submit" className="photo-button" disabled={isCreatingProspect}>
                      {isCreatingProspect ? 'Création...' : 'Créer et continuer'}
                    </button>
                    <button
                      type="button"
                      className="photo-button secondary"
                      onClick={() => { setShowNouveauClientForm(false); resetNouveauClientForm(); }}
                      disabled={isCreatingProspect}
                    >
                      Annuler
                    </button>
                  </div>
                </form>
              )}

              {isLoadingClients ? (
                <div className="loading">Chargement des clients...</div>
              ) : clients.length === 0 ? (
                <div className="no-clients">Aucun client dans cette zone</div>
              ) : clientsWithoutGps === 0 ? (
                <div className="no-clients">Tous les clients de cette zone ont déjà été géolocalisés !</div>
              ) : (
                <>
                  <div className="clients-stats">
                    <span className="stat-badge">{clientsWithoutGps} à géolocaliser</span>
                  </div>
                  <div className="client-search-wrapper">
                    <input
                      type="text"
                      placeholder="Rechercher un client..."
                      value={clientSearch}
                      onChange={(e) => setClientSearch(e.target.value)}
                      className="client-search-input"
                    />
                    {clientSearch && (
                      <button
                        className="client-search-clear"
                        onClick={() => setClientSearch('')}
                        type="button"
                      >✕</button>
                    )}
                  </div>
                  <select
                    value={selectedClient ? selectedClient.id : ''}
                    onChange={(e) => {
                      const client = clients.find(c => c.id === parseInt(e.target.value));
                      if (client) handleClientSelect(client);
                    }}
                    className="client-select"
                  >
                    <option value="">-- Sélectionner un client --</option>
                    {filteredClients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.nom_client}
                      </option>
                    ))}
                  </select>
                  {clientSearch && filteredClients.length === 0 && (
                    <div className="no-clients-search">Aucun client trouvé pour "{clientSearch}"</div>
                  )}
                  {selectedClient && (
                    <div className="selected-client-details">
                      <div className="client-detail-item">
                        <strong>Client:</strong> {selectedClient.nom_client}
                      </div>
                      {selectedClient.localisation && (
                        <div className="client-detail-item">
                          <strong>Localisation:</strong> {selectedClient.localisation}
                        </div>
                      )}
                      {selectedClient.contact && (
                        <div className="client-detail-item">
                          <strong>Contact:</strong> {selectedClient.contact}
                        </div>
                      )}
                      {selectedClient.latitude && selectedClient.longitude && (
                        <div className="client-detail-item">
                          <strong>GPS:</strong> Lat: {selectedClient.latitude.toFixed(4)}, Lng: {selectedClient.longitude.toFixed(4)}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Étape 3: Capture des coordonnées */}
        {selectedClient && (
          <div className="step-container action-step">
            <div className="step-header">
              <span className="step-number">3</span>
              <h3>Géolocaliser</h3>
            </div>
            <div className="step-content">
              {/* Sélection de photo du PDV */}
              <div className={`photo-section ${photoPreview ? 'has-photo' : ''}`}>
                <h4>📷 Photo du point de vente <span style={{color: '#dc3545'}}>*</span></h4>
                {!photoPreview ? (
                  <div className="photo-upload">
                    <button
                      onClick={openCamera}
                      className="photo-button"
                      disabled={isCapturing || isUploadingPhoto}
                    >
                      📷 Prendre une photo
                    </button>
                    <p className="photo-hint">Photo obligatoire - La caméra s'ouvrira directement</p>
                  </div>
                ) : (
                  <div className="photo-preview-container">
                    <img src={photoPreview} alt="Aperçu PDV" className="photo-preview" />
                    <div className="photo-actions">
                      <button onClick={clearPhoto} className="photo-button secondary">
                        ❌ Changer la photo
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Modal de caméra */}
              {showCameraModal && (
                <div className="camera-modal" onClick={closeCamera}>
                  <div className="camera-modal-content" onClick={(e) => e.stopPropagation()}>
                    <div className="camera-modal-header">
                      <h3>📷 Caméra</h3>
                      <button onClick={closeCamera} className="close-button">✕</button>
                    </div>
                    <div className="camera-preview">
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        style={{ width: '100%', maxHeight: '400px', objectFit: 'cover' }}
                      />
                      <canvas ref={canvasRef} style={{ display: 'none' }} />
                    </div>
                    <div className="camera-modal-footer">
                      <button onClick={capturePhotoFromCamera} className="photo-button">
                        📸 Capturer
                      </button>
                      <button onClick={closeCamera} className="photo-button secondary">
                        ✗ Annuler
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <button
                onClick={captureCoordinates}
                disabled={isCapturing}
                className={`capture-button ${isCapturing ? 'capturing' : ''}`}
              >
                {isCapturing ? '🔄 Recherche...' : 'Géolocaliser'}
              </button>

              {captureStatus && (
                <div className={`status-message ${captureStatus.includes('✅') ? 'success' : captureStatus.includes('❌') ? 'error' : 'info'}`}>
                  {captureStatus}
                </div>
              )}

              {currentPosition && (
                <div className="position-result">
                  <h4>Position capturée:</h4>
                  <div className="position-details">
                    <div><span className="label">Latitude:</span> {currentPosition.latitude.toFixed(6)}</div>
                    <div><span className="label">Longitude:</span> {currentPosition.longitude.toFixed(6)}</div>
                    <div><span className="label">Précision:</span> ±{Math.round(currentPosition.accuracy)}m</div>
                  </div>
                  {currentPosition.accuracy > 50 && (
                    <div className="warning-box">
                      ⚠️ Précision faible. Pour une meilleure précision, allez à l'extérieur ou près d'une fenêtre.
                    </div>
                  )}
                  {currentPosition.accuracy <= 50 && (
                    <div className="success-box">
                      ✅ Bonne précision ! Vous pouvez enregistrer ces coordonnées.
                    </div>
                  )}
                </div>
              )}

            </div>
          </div>
        )}
      </main>

      <footer className="app-footer">
        <p>MerchFlow - Application de Géolocalisation © 2026</p>
      </footer>
    </div>
  );
}

export default GpsCapturePage;
